import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { useDispatch, useStore } from "react-redux";
import MaterialIcon from "../../../components/MaterialIcon";
import { callAPI } from "../../../helper/apiUtils";
import { $crud } from "../../../helper/CrudFactory";
import { getBuildingChat } from "../../../helper/buildingChat";
import { fetchChatPage } from "../../../helper/chatMessageCache";
import { decryptMessage } from "../../../utils/encryptDecryptFunction";
import { locateChatMessage, openChatMessage, openEmail, openProjectTask } from "../cockpitNavigation";
import {
  STOP_WORDS as SOURCE_STOP,
  findSimilar,
  isSamePriority,
  normalizeTaskText,
  parsePriorityDeadline,
  priorityDateValue,
  priorityText,
  BOOKMARK_CATEGORIES,
  COCKPIT_TASK_GROUPS,
  cockpitGroupOf,
  cockpitGroupTitles,
} from "../priorityBookmarks";
import { BookmarkRow, RowMenu, useBookmarkActions } from "./CockpitTodoLists";
import { useTodoCalendar } from "../../Feeds/useTodoCalendar";
import { CockpitTaskContext } from "./CockpitTaskView";

/** Scroll target for the "Vorgemerkte Aufgaben" KPI tile. */
export const BOOKMARKED_PRIORITIES_ID = "cockpit-bookmarked-priorities";

function plain(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeName(value) {
  return plain(value)
    .toLowerCase()
    .replace(/^['"„“«]+|['"“”»]+$/g, "")
    .replace(/^projekt\s+/i, "")
    .trim();
}

function matchRoom(rooms, projectName, report) {
  const needle = normalizeName(projectName);
  if (!needle) return null;
  const list = Array.isArray(rooms) ? rooms : [];
  // The exact name first: with every project in the list a short name can be
  // part of a longer one.
  const exact = list.find((room) => normalizeName(room && room.name) === needle);
  if (exact) return exact;
  const hit = list.find((room) => {
    const name = normalizeName(room && room.name);
    return name && (name === needle || name.includes(needle) || needle.includes(name));
  });
  if (hit) return hit;
  const linked = Array.isArray(report && report.linkedProjects) ? report.linkedProjects : [];
  const linkedHit = linked.find((project) => {
    const name = normalizeName(project && project.name);
    return name && (name === needle || name.includes(needle) || needle.includes(name));
  });
  if (linkedHit) return linkedHit;
  if (report && report.buildingId && normalizeName(report.roomName) && (needle.includes(normalizeName(report.roomName)) || normalizeName(report.roomName).includes(needle))) {
    return { id: report.buildingId, name: report.roomName };
  }
  return null;
}

function readPriorities(table) {
  return [...table.querySelectorAll("tr")]
    .filter((row) => row.querySelector("td"))
    .map((row) => {
      const cells = [...row.querySelectorAll("td")].map((cell) => plain(cell.textContent));
      if (cells.length < 2 || !cells[1]) return null;
      return {
        project: cells[0],
        nextStep: cells[1],
        deadline: cells[2] || "",
        suggestedBy: cells[3] || "",
        sourceType: row.getAttribute("data-source-type") || "",
        emailId: row.getAttribute("data-email-id") || "",
        emailAccountId: row.getAttribute("data-email-account") || "",
        chatId: row.getAttribute("data-chat-id") || "",
        groupId: row.getAttribute("data-group-id") || "",
        roomId: row.getAttribute("data-room-id") || "",
        companyId: row.getAttribute("data-company-id") || "",
        // Hidden rows for the report's open decisions / critical points.
        kind: row.getAttribute("data-kind") || "",
        // What the priority is based on (reports from 2026-10-30 on).
        detail: plain(row.getAttribute("data-detail") || ""),
      };
    })
    .filter(Boolean);
}

/** Abbreviations a German noun often follows ("Putz- bzw. Spachtelarbeiten"). */
const SENTENCE_ABBREVIATIONS = new Set([
  "bzw", "ca", "inkl", "exkl", "ggf", "evtl", "usw", "etc", "vgl", "gem", "lt", "zzgl", "abzgl",
  "nr", "str", "tel", "hr", "fr", "dr", "prof", "ing", "dipl", "insb", "sog", "bspw", "max", "min",
  "mind", "jew", "geb", "gez", "abs", "ziff", "anh", "kap", "inh", "kw", "st", "stk", "pos",
]);

/**
 * Where sentences end in a text: at ". X", not at the dot of a date, a number
 * or an abbreviation ("01.08. 16 Uhr", "Raum 3.14", "z. B. Fenster", "bzw.
 * Spachtel"). `withParen` also ends one at "… (Nr. 3456). Die …".
 */
function sentenceEnds(text, withParen) {
  const boundary = withParen ? /(?:[^\d.\s]{2}|\d\))[.!?](?=\s+\p{Lu})/gu : /[^\d.\s]{2}[.!?](?=\s+\p{Lu})/gu;
  const ends = [];
  let match;
  while ((match = boundary.exec(text))) {
    const end = match.index + match[0].length;
    const word = /(\p{L}+)\.$/u.exec(text.slice(0, end));
    if (word && SENTENCE_ABBREVIATIONS.has(word[1].toLowerCase())) continue;
    ends.push(end);
  }
  return ends;
}

/**
 * A priority as one sentence: the first sentence of its next step. The whole
 * step stays in the row's tooltip.
 */
function prioritySentence(row) {
  const text = plain(row && row.nextStep);
  if (!text) return "";
  const [end] = sentenceEnds(text, false);
  return end ? text.slice(0, end) : text;
}

/** A text as sentences, cut where `prioritySentence` ends one ("… (Nr. 3456). Die …" too). */
function sentencesOf(text) {
  const body = plain(text);
  const out = [];
  let start = 0;
  sentenceEnds(body, true).forEach((end) => {
    out.push(body.slice(start, end).trim());
    start = end;
  });
  out.push(body.slice(start).trim());
  return out.filter(Boolean);
}

/**
 * The words (as stems) and figures of a text, each mapped to whether it is a
 * figure: "3.14", "2789" or "04.11.2026" — three digits or more, so times and
 * floors ("15:00", "5. OG") do not count.
 */
function detailTokens(text) {
  const tokens = new Map();
  (plain(text).toLowerCase().match(/[\p{L}\p{N}]+(?:[.,]\p{N}+)*/gu) || []).forEach((token) => {
    if (/\p{N}/u.test(token)) {
      if (token.replace(/\D/g, "").length >= 3) tokens.set(token, true);
      return;
    }
    if (token.length < 4 || SOURCE_STOP.has(token)) return;
    const stem = token.length >= 6 ? token.slice(0, Math.max(5, Math.ceil(token.length * 0.7))) : token;
    if (!SOURCE_STOP.has(stem)) tokens.set(stem, false);
  });
  return tokens;
}

/**
 * Detail for a priority of a report written before rows carried their own:
 * the summary's sentences about it, at most two, in the summary's order. A
 * shared figure counts 3, a word only that sentence has 2, any other shared
 * word 1; a sentence needs 3.
 */
function detailFromSummary(row, summaryText) {
  const wanted = [...detailTokens(row.nextStep)];
  if (!wanted.length) return "";
  const shown = normalizeTaskText(prioritySentence(row));
  const sentences = sentencesOf(summaryText).map((sentence) => ({ sentence, tokens: detailTokens(sentence) }));
  const weights = wanted.map(([token, figure]) => {
    const hits = sentences.filter((item) => item.tokens.has(token)).length;
    return [token, figure ? 3 : hits === 1 ? 2 : 1];
  });
  return sentences
    .map(({ sentence, tokens }, index) => ({
      sentence,
      index,
      score: weights.reduce((sum, [token, weight]) => (tokens.has(token) ? sum + weight : sum), 0),
    }))
    .filter((item) => item.score >= 3 && normalizeTaskText(item.sentence) !== shown)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, 2)
    .sort((a, b) => a.index - b.index)
    .map((item) => item.sentence)
    .join(" ");
}

/**
 * The paragraph under an opened priority: the rest of its step after the
 * sentence the list shows, then what the report based it on.
 */
function priorityDetail(row, summaryText) {
  const step = plain(row.nextStep);
  const rest = step.slice(prioritySentence(row).length).trim();
  const basis = row.detail || detailFromSummary(row, summaryText);
  return [rest, basis && normalizeTaskText(basis) !== normalizeTaskText(step) ? basis : ""].filter(Boolean).join(" ");
}

/** A date as the local "YYYY-MM-DD", else "". */
function localDayKey(value) {
  const date = new Date(value || "");
  if (!value || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Completed by the assignee or confirmed by the creator. */
function isDoneTask(task) {
  return ["completed", "confirmed"].includes(String((task && task.status) || ""));
}

/**
 * A dated task due after today. The cockpit lists the day's work only, so a
 * task moved to a later day (here, on the Aufgaben page or in Todoist) leaves
 * it; overdue ones stay. Bookmarks have no day and always stay.
 */
function dueAfterToday(task) {
  if (!task || task.isBookmarked == null || Number(task.isBookmarked)) return false;
  const due = new Date(task.createdAt || "");
  if (Number.isNaN(due.getTime())) return false;
  const endOfToday = new Date();
  endOfToday.setHours(24, 0, 0, 0);
  return due >= endOfToday;
}

function isFazitTitle(title) {
  return /fazit|management/i.test(String(title || ""));
}

function isAppointmentTitle(title) {
  return /termin|appointment/i.test(String(title || ""));
}

function isMembersTitle(title) {
  return /beteiligte|engaged/i.test(String(title || ""));
}

/**
 * The project rooms a report actually covers — the same set the card counts
 * as "Aktive Projekträume". Names come from the priority rows, so a room only
 * appears once the report has something to say about it.
 */
export function roomsCoveredByReport(report, rooms) {
  const html = report && (report.bodyHtml || report.html || "");
  const model = parseBriefing(html) || fallbackFromText(report && (report.bodyText || report.body || ""));
  if (!model) return [];
  const byId = new Map();
  (model.sections || []).forEach((section) => {
    if (isFazitTitle(section.title) || isAppointmentTitle(section.title) || isMembersTitle(section.title)) return;
    (section.priorities || []).forEach((row) => {
      const name = plain(row && row.project);
      if (!name || /^[-–—]+$/.test(name)) return;
      const room = matchRoom(rooms, name, report);
      if (!room || room.id == null) return;
      if (!byId.has(String(room.id))) byId.set(String(room.id), room);
    });
  });
  return [...byId.values()];
}

/**
 * A paragraph as text runs, keeping the report's bold ("**KBN:** Die beiden
 * Zahlungen …"), so the card can show it without rendering report HTML.
 */
function richRuns(node) {
  const runs = [];
  const walk = (current, bold) => {
    current.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        if (child.textContent) runs.push({ text: child.textContent, bold });
      } else if (child.nodeType === 1) {
        const tag = child.tagName.toLowerCase();
        if (tag === "br") runs.push({ text: " ", bold });
        else walk(child, bold || tag === "strong" || tag === "b");
      }
    });
  };
  walk(node, false);
  // Collapse whitespace across runs like `plain` does for the whole text.
  const out = [];
  runs.forEach((run) => {
    const text = run.text.replace(/\s+/g, " ");
    const last = out[out.length - 1];
    if (last && last.bold === run.bold) last.text += text;
    else out.push({ text, bold: run.bold });
  });
  if (out.length) {
    out[0].text = out[0].text.replace(/^\s+/, "");
    out[out.length - 1].text = out[out.length - 1].text.replace(/\s+$/, "");
  }
  return out.filter((run) => run.text);
}

/**
 * A paragraph's runs cut before each bold "Room:" label after its start: the
 * woven summary names the rooms mid-text ("… vor Einbau. **Krug Consult:** In
 * Raum …"), and each room starts on its own line.
 */
function splitAtRoomLabels(runs) {
  const parts = [[]];
  runs.forEach((run, i) => {
    if (i > 0 && run.bold && /:\s*$/.test(run.text)) parts.push([]);
    parts[parts.length - 1].push(run);
  });
  return parts
    .map((part) => part.map((run, i) => {
      let text = run.text;
      if (i === 0) text = text.replace(/^\s+/, "");
      if (i === part.length - 1) text = text.replace(/\s+$/, "");
      return { ...run, text };
    }).filter((run) => run.text))
    .filter((part) => part.length);
}

function readCopy(cell) {
  const clone = cell.cloneNode(true);
  const heading = clone.querySelector("h2");
  if (heading) heading.remove();
  clone.querySelectorAll("table").forEach((table) => table.remove());
  const items = [...clone.querySelectorAll("li")].map((item) => plain(item.textContent)).filter(Boolean);
  if (items.length) return { items, paragraphs: [] };
  const nodes = [...clone.querySelectorAll("p")].filter((p) => plain(p.textContent));
  if (nodes.length) {
    return { items: [], paragraphs: nodes.map((p) => plain(p.textContent)), rich: nodes.map(richRuns) };
  }
  const text = plain(clone.textContent);
  return { items: [], paragraphs: text ? [text] : [] };
}

function parseBriefing(html) {
  if (!html || typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  const banner = [...doc.querySelectorAll("td")].find((cell) => /0b1f33/i.test(cell.getAttribute("style") || ""));
  const bannerLines = banner ? [...banner.querySelectorAll("div")].map((line) => plain(line.textContent)).filter(Boolean) : [];
  const kpis = [...doc.querySelectorAll("td")]
    .filter((cell) => /font-size:\s*26px/i.test(cell.getAttribute("style") || ""))
    .map((cell) => {
      const table = cell.closest("table");
      const lines = table ? [...table.querySelectorAll("td")].map((item) => plain(item.textContent)).filter(Boolean) : [];
      return lines.length ? { value: lines[0], label: lines[1] || "", hint: lines[2] || "" } : null;
    })
    .filter(Boolean);

  const sections = [...doc.querySelectorAll("h2")].map((heading) => {
    const title = plain(heading.textContent);
    const cell = heading.parentElement;
    const table = cell ? cell.querySelector("table") : null;
    const priorities = table && /priorit/i.test(title) ? readPriorities(table) : null;
    const copy = cell ? readCopy(cell) : { items: [], paragraphs: [] };
    return { title, priorities, ...copy };
  }).filter((section) => section.title);

  if (!bannerLines.length && !kpis.length && !sections.length) return null;
  return {
    title: bannerLines[0] || "",
    status: bannerLines[1] || "",
    kpis,
    sections,
  };
}

const HOUR_MS = 60 * 60 * 1000;
// A report summarises the 24 hours before it was written (plus slack for late
// runs); older sources need much stronger text evidence.
const REPORT_WINDOW_MS = 30 * HOUR_MS;
const CHAT_PAGE_SIZE = 30;
const CHAT_GROUPS_TO_SCAN = 8;
const CHAT_PAGES_TO_SCAN = 24;

/** "email" / "chat" when the report links the source or names an address, "" when unknown. */
function sourceKind(row) {
  if (row.sourceType === "email" || row.emailId) return "email";
  if (row.sourceType === "chat" || row.groupId || row.chatId) return "chat";
  return /@/.test(row.suggestedBy || "") ? "email" : "";
}

const SOURCE_BUTTON = {
  email: { icon: "mail", label: "navigate_to_source", fallback: "Zur Quelle navigieren" },
  chat: { icon: "forum", label: "navigate_to_source", fallback: "Zur Quelle navigieren" },
  "": { icon: "link", label: "navigate_to_source", fallback: "Zur Quelle navigieren" },
};

/**
 * A bookmarked to-do described the way the priority-source lookup expects, so
 * one the user made from a chat or a mail can offer the same jump as a row
 * the report wrote. Null when the to-do has no source to jump to.
 */
function sourceRowFromTask(task) {
  const emailId = task.emailMessageId || task.emailId || "";
  const groupId = task.groupId || task.chatId || task.chat || "";
  if (!emailId && !groupId) return null;
  return {
    project: task.roomName || "",
    nextStep: task.description || "",
    deadline: "",
    suggestedBy: "",
    sourceType: emailId ? "email" : "chat",
    emailId: emailId ? String(emailId) : "",
    emailAccountId: task.emailAccountId || null,
    chatId: emailId ? "" : String(task.messageId || ""),
    groupId: emailId ? "" : String(groupId),
    roomId: task.buildingList || task.roomId || task.buildingId || "",
    // The to-do API already resolved the transcript page; it is only a
    // starting point, because messages sent since then shift the paging.
    chatPage: Number(task.messagePageNumber) > 0 ? Number(task.messagePageNumber) : 0,
  };
}

function wordsOf(text) {
  return plain(text).toLowerCase().split(/[^\p{L}\p{N}]+/u);
}

/** Stems of the priority text, without the person it is attributed to. */
function sourceStems(row) {
  const person = new Set(wordsOf(row.suggestedBy));
  const stems = new Set();
  wordsOf(row.nextStep)
    .filter((word) => word.length >= 4 && !SOURCE_STOP.has(word) && !person.has(word))
    .forEach((word) => {
      const stem = word.length >= 6 ? word.slice(0, Math.max(5, Math.ceil(word.length * 0.7))) : word;
      if (!SOURCE_STOP.has(stem)) stems.add(stem);
    });
  return [...stems];
}

function samePerson(expected, actual) {
  const a = plain(expected).toLowerCase();
  const b = plain(actual).toLowerCase();
  if (a.length < 3 || b.length < 3) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const surname = (value) =>
    value.split(/[\s,()<>]+/).filter((part) => part.length >= 3 && !part.includes("@")).pop() || "";
  const last = surname(a);
  return !!last && last === surname(b);
}

function timeOf(value) {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
}

// A stem or sender only one or two candidates share identifies the source; one
// that most of them share (e.g. the user's own name) barely does.
function rarityWeight(hits) {
  if (hits === 1) return 4;
  return hits <= 3 ? 2 : 1;
}

/**
 * Best email / chat candidate for a priority row, or null. Anything written
 * after the report cannot be its source; anything older than its window needs
 * more evidence. On a tie the email wins.
 */
function bestSource(candidates, row, reportAt) {
  const usable = candidates.filter((item) => timeOf(item.at) <= reportAt + HOUR_MS);
  const hays = usable.map((item) => plain(item.text).toLowerCase());
  const stems = sourceStems(row).map((stem) => {
    const hits = hays.filter((hay) => hay.includes(stem)).length;
    return { stem, weight: rarityWeight(hits) * (stem.length >= 8 ? 2 : 1) };
  });
  const sentBy = usable.map((item) => samePerson(row.suggestedBy, item.person));
  const senderWeight = rarityWeight(sentBy.filter(Boolean).length);
  const ranked = usable
    .map((item, index) => {
      const content = stems.reduce((sum, { stem, weight }) => (hays[index].includes(stem) ? sum + weight : sum), 0);
      if (!content) return null;
      const evidence = content + (sentBy[index] ? senderWeight : 0);
      const inWindow = timeOf(item.at) >= reportAt - REPORT_WINDOW_MS;
      if (evidence < (inWindow ? 3 : 5)) return null;
      return { item, score: evidence + (inWindow ? 2 : 0) };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        b.score - a.score ||
        (a.item.kind === "email" ? 0 : 1) - (b.item.kind === "email" ? 0 : 1) ||
        timeOf(b.item.at) - timeOf(a.item.at)
    );
  return ranked.length ? ranked[0].item : null;
}

function listedRooms(state) {
  const list = ((state && state.projects) || []).map((room) => ({ ...room, isInternal: false }));
  ((state && state.internalProjectsAll) || []).forEach((company) => {
    (company.userProjectList || []).forEach((room) => {
      list.push({ ...room, isInternal: true, companyId: company.id });
    });
  });
  return list;
}

/** Room of a priority row, with the internal / company flags the deeplink needs. */
function resolveRoom(row, rooms, report, state) {
  const known = [...(Array.isArray(rooms) ? rooms : []), ...listedRooms(state)];
  const linked = Array.isArray(report && report.linkedProjects) ? report.linkedProjects : [];
  const reportRoomId = (report && report.buildingId) || (linked.length === 1 ? linked[0].id : null);
  const picked = row.roomId
    ? { id: row.roomId }
    : matchRoom(known, row.project, report) || (reportRoomId ? { id: reportRoomId } : null);
  const id = picked && (picked.id || picked.buildingId);
  if (!id) return null;
  const internal = known.find((room) => room.isInternal && String(room.id) === String(id));
  const isInternal = !!(internal || picked.isInternal);
  const companyId = isInternal ? (internal && internal.companyId) || picked.companyId : null;
  return {
    id: String(id),
    isInternal,
    companyId: companyId ? String(companyId) : undefined,
  };
}

async function readChatPage(groupId, page, roomId) {
  const res = await fetchChatPage({ chatId: groupId, page, limit: CHAT_PAGE_SIZE, building: roomId });
  const data = res && res.data;
  if (!data || !data.status) return null;
  return {
    messages: Array.isArray(data.chatHistory) ? data.chatHistory : [],
    secretKey: (data.groupInfo && data.groupInfo.secretKey) || "",
    end: !!data.chatEnd,
  };
}

function chatText(msg, secretKey) {
  const body = secretKey ? decryptMessage(msg.message, secretKey) : msg.message;
  return [body, msg.transcribedAudio, msg.imageDescription, msg.docFileSummary, msg.attachedFileName]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");
}

/** Emails filed in the room, received and sent. */
async function emailCandidates(room) {
  const lists = await Promise.all(
    ["incoming", "outgoing"].map((direction) =>
      callAPI(`/email/projects/${encodeURIComponent(room.id)}/emails`, direction === "outgoing" ? { direction } : {}, "GET")
        .then((res) => (res && res.data && Array.isArray(res.data.data) ? res.data.data : []))
        .catch(() => [])
    )
  );
  const seen = new Set();
  return []
    .concat(...lists)
    .filter((item) => {
      const id = item && item.messageId ? String(item.messageId) : "";
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((item) => {
      const from = item.fromAddress || {};
      return {
        kind: "email",
        id: item.messageId,
        emailAccountId: item.emailAccount || null,
        at: item.receivedAt,
        text: [item.subject, item.snippet, item.bodyPreview].filter(Boolean).join(" "),
        person: [from.name, from.email || from.address, item.senderEmail, item.direction === "outgoing" ? item.to : ""]
          .filter(Boolean)
          .join(" "),
      };
    });
}

/**
 * Messages of the room's chats, from the newest back to the start of the
 * report window. `groupId` is the most recently active chat.
 */
async function chatCandidates(room, reportAt, userId) {
  const res = await getBuildingChat({ buildingId: room.id, userId });
  const groups = res && res.data && Array.isArray(res.data.groups) ? res.data.groups : [];
  const since = reportAt - REPORT_WINDOW_MS;
  const active = groups
    .filter((group) => group && group.id && timeOf(group.messageDate) >= since)
    .slice(0, CHAT_GROUPS_TO_SCAN);
  const list = [];
  let pagesLeft = CHAT_PAGES_TO_SCAN;
  await Promise.all(
    active.map(async (group) => {
      for (let page = 1; pagesLeft > 0; page += 1) {
        pagesLeft -= 1;
        const chunk = await readChatPage(group.id, page, room.id);
        if (!chunk) return;
        chunk.messages.forEach((msg) => {
          if (!msg || !msg.id || msg.isDeleted || msg.leftGroup || msg.joinedGroup || msg.pendingGroup) return;
          list.push({
            kind: "chat",
            groupId: group.id,
            messageId: msg.id,
            page,
            at: msg.createdAt,
            text: chatText(msg, chunk.secretKey),
            person: msg.senderName,
          });
        });
        const oldest = chunk.messages.length ? timeOf(chunk.messages[0].createdAt) : 0;
        if (chunk.end || chunk.messages.length < CHAT_PAGE_SIZE || oldest <= since) return;
      }
    })
  );
  return { list, groupId: active.length ? active[0].id : null };
}

export function prioritiesFromEmail(email) {
  const html = email && (email.bodyHtml || email.html || "");
  const model = parseBriefing(html) || fallbackFromText(email && (email.bodyText || email.body || email.snippet || ""));
  if (!model) return [];
  const rows = [];
  (model.sections || []).forEach((section) => {
    (section.priorities || []).forEach((row) => {
      if (row && (row.nextStep || row.project)) rows.push(row);
    });
  });
  return rows;
}

function fallbackFromText(text) {
  const body = String(text || "").trim();
  if (!body) return null;
  const blocks = body.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  return {
    title: blocks[0] || "",
    status: "",
    kpis: [],
    sections: blocks.slice(1).map((block) => {
      const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
      const title = lines[0].replace(/:\s*$/, "");
      const rest = lines.slice(1);
      const bullets = rest.filter((line) => /^[-•]\s+/.test(line)).map((line) => line.replace(/^[-•]\s+/, ""));
      return {
        title,
        priorities: null,
        items: bullets,
        paragraphs: bullets.length ? [] : [rest.join(" ") || ""],
      };
    }),
  };
}

/**
 * The stored briefing is an HTML mail. On the cockpit it is shown as text,
 * with a to-do action on every priority row.
 */
/**
 * `tasksTarget` (optional): a DOM node outside the report — the cockpit's
 * "Meine Aufgaben" card. The task list is drawn there through a portal and
 * keeps its state and handlers here. The card brings its own heading, so the
 * list drops its own and puts "Mehrere löschen" into `tasksActionTarget`, the
 * card's header slot.
 * `onGroupCounts({ appointment, tasks })` gets the number of rows under
 * "Termine" and "Fristen & Aufgaben" after each render (null once the list is
 * gone), for the cockpit's two chips.
 * `taskGroups` (["tasks", "appointment"], optional): the tiles switched on
 * on the cockpit — the list shows only those groups (the counts keep both).
 * `taskDay` ("YYYY-MM-DD", optional): a later day picked in the cockpit's day
 * strip — the list shows only what falls on it; null is today's list.
 * `hideProject`: a single project's report — every priority is that project's,
 * so the top five drop the project label.
 * `fazitMeta`: a line under the title (the report's "Stand").
 * `prioritiesOnly` (cockpit and a project's Übersicht): the top five without
 * the summary text, under `fazitTitle`; a click opens a priority's detail
 * paragraph instead of its task (the detail has the button for that).
 * `priorityCards` (with prioritiesOnly, a project's "Heute wichtig"): the top
 * five as cards in one row that scrolls sideways, each with its detail and
 * "Aufgabe öffnen".
 */
export default function CockpitBriefingText({ email, rooms, strings, noted, dismissed, bookmarks, onCreated, onDismiss, onTaskChanged, toolbar, tasksTarget = null, tasksActionTarget = null, summaryOnly = false, onGroupCounts = null, taskGroups = null, taskDay = null, fazitTitle = null, fazitMeta = null, hideProject = false, prioritiesOnly = false, priorityCards = false, taskLinks = null, onOpenTask = null }) {
  const [rowState, setRowState] = useState({});
  // The card row (priorityCards) and whether it can scroll further each way.
  const cardsRef = useRef(null);
  const [cardsScroll, setCardsScroll] = useState({ back: false, next: false, first: 0 });
  // The opened priority of the top five (prioritiesOnly), by its key.
  const [openPriority, setOpenPriority] = useState(null);
  const [sourceState, setSourceState] = useState({});
  // null while the list is in its normal state; a Set of row keys once the
  // user is picking rows to delete.
  const [selection, setSelection] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Report rows being deleted: off the list at once, back only if the delete fails.
  const [hiddenTexts, setHiddenTexts] = useState([]);
  const sourceBusyRef = useRef(false);
  const linkedRef = useRef({});
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const openTaskOnCockpit = useContext(CockpitTaskContext);
  const tasks = useBookmarkActions({ strings, onChanged: onTaskChanged });
  // The calendar button on a task row (the ToDo page's "Add to calendar").
  const calendar = useTodoCalendar({ onChange: tasks.patchItem, onScheduled: tasks.scheduled });
  const model = useMemo(() => {
    const html = email && (email.bodyHtml || email.html || "");
    return parseBriefing(html) || fallbackFromText(email && (email.bodyText || email.body || ""));
  }, [email]);
  // Set while rendering the task list below; passed up once the render is done.
  const groupCountsRef = useRef(null);
  useEffect(() => {
    if (onGroupCounts) onGroupCounts(groupCountsRef.current);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => onGroupCounts && onGroupCounts(null), []);
  // Another tab: rows picked under the other one are no longer on screen.
  const taskGroupsKey = taskGroups ? taskGroups.join(",") : "";
  useEffect(() => setSelection(null), [taskGroupsKey, taskDay]);
  // The arrows show only while there is more of the card row that way;
  // `first` is the card at the row's left edge ("1 of 5 priorities").
  const measureCards = () => {
    const el = cardsRef.current;
    if (!el) return;
    const back = el.scrollLeft > 1;
    const next = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    const card = el.querySelector(".cockpit-priority-card");
    const step = card ? card.getBoundingClientRect().width + 12 : 0;
    const first = step ? Math.round(el.scrollLeft / step) : 0;
    setCardsScroll((prev) => (prev.back === back && prev.next === next && prev.first === first ? prev : { back, next, first }));
  };
  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return undefined;
    measureCards();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measureCards);
    observer.observe(el);
    return () => observer.disconnect();
  });
  const scrollCards = (direction) => {
    const el = cardsRef.current;
    if (!el) return;
    const card = el.querySelector(".cockpit-priority-card");
    const step = card ? card.getBoundingClientRect().width + 12 : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };
  groupCountsRef.current = null;

  if (!model) return null;

  const sections = model.sections || [];
  const fazit = sections.find((section) => isFazitTitle(section.title)) || null;
  // Tasks the user completed or confirmed leave the cockpit: the top five and
  // the task list both drop the report rows they came from.
  // One entry per task text: a sentence that ended up as a bookmark and as a
  // dated task (two cockpits at once) is shown once — the open one first.
  const allTasks = tasks
    .resolve(bookmarks || [])
    .slice()
    .sort((a, b) => Number(isDoneTask(a)) - Number(isDoneTask(b)))
    .filter((item, index, list) => {
      const text = normalizeTaskText(item.description);
      return !text || list.findIndex((other) => normalizeTaskText(other.description) === text) === index;
    });
  const openTasks = allTasks.filter((item) => !isDoneTask(item));
  const doneTasks = allTasks.filter(isDoneTask);
  const rowIsDone = (row) => {
    const text = priorityText(row);
    return !!findSimilar(doneTasks, text, (item) => item.description) && !findSimilar(openTasks, text, (item) => item.description);
  };
  // The report's top five, one line each under the fazit. The hidden rows
  // after them (decisions, critical points — `kind`) are tasks, not priorities.
  // A step the report lists twice is shown once.
  const topFive = sections
    .reduce((rows, section) => rows.concat(section.priorities || []), [])
    .filter((row) => row && !row.kind && (row.nextStep || (row.project && !hideProject)))
    .filter((row, index, rows) => !rows.slice(0, index).some((other) => isSamePriority(priorityText(other), priorityText(row))))
    .filter((row) => !rowIsDone(row))
    .slice(0, 5);
  // A task opens on the cockpit (its detail, back returns to the overview);
  // elsewhere (a project's Übersicht) inside its project, on Aufgaben.
  const openTaskInProject = (item) => {
    if (!item || item.todoId == null) return;
    if (openTaskOnCockpit) {
      openTaskOnCockpit(item);
      return;
    }
    openProjectTask(dispatch, history, {
      todoId: item.todoId,
      roomId: item.roomId || item.buildingList || null,
      roomName: item.roomName || "",
    });
  };
  // A click opens the task the row produced inside its project (Aufgaben), or
  // the project's Aufgaben while the task does not exist yet. A row without a
  // project has no task to open. A project's Übersicht has no task list here:
  // `taskLinks` ([{ id, text, roomId }]) name the task each priority produced.
  const openTopTask = (row) => {
    const task = findSimilar(openTasks, priorityText(row), (item) => item.description);
    if (task) {
      openTaskInProject(task);
      return;
    }
    const link = findSimilar(taskLinks, priorityText(row), (entry) => entry.text);
    // Inside the project (`onOpenTask`) the task opens in place: no detour
    // through the project list, the pinned project stays as it is.
    if (onOpenTask) {
      onOpenTask(link ? link.id : null);
      return;
    }
    if (link) {
      openTaskInProject({ todoId: link.id, roomId: link.roomId });
      return;
    }
    const room = resolveRoom(row, rooms, email, store.getState());
    if (room) openProjectTask(dispatch, history, { roomId: room.id, isInternal: room.isInternal, companyId: room.companyId });
  };
  const summaryText = fazit ? fazit.paragraphs.join(" ") : "";
  const topKey = (row, index) => `${row.project}|${row.nextStep}|${index}`;
  // The report's project cell is the model's guess; once the row produced a
  // task, the task's project is the one that counts (it may have been moved).
  const roomNameById = (id) => {
    if (id == null || id === "") return "";
    const known = [...(Array.isArray(rooms) ? rooms : []), ...listedRooms(store.getState())];
    const room = known.find((r) => r && String(r.id) === String(id));
    return (room && room.name) || "";
  };
  const rowProject = (row) => {
    const task = findSimilar(openTasks, priorityText(row), (item) => item.description);
    if (task) {
      const name = task.roomName || roomNameById(task.roomId || task.buildingList || task.buildingId);
      if (name) return name;
    }
    const link = findSimilar(taskLinks, priorityText(row), (entry) => entry.text);
    return roomNameById(link && link.roomId) || roomNameById(row.roomId) || row.project;
  };
  const topRowText = (row) => {
    const project = hideProject ? "" : rowProject(row);
    return (
      <span className="cockpit-briefing-top-text" title={[project, row.nextStep].filter(Boolean).join(": ")}>
        {project ? <strong>{project}</strong> : null}
        {project && row.nextStep ? " · " : null}
        {prioritySentence(row)}
      </span>
    );
  };
  // Each priority opens in place: its detail, who it came from, and the task.
  const renderOpenableTop = (row, index) => {
    const key = topKey(row, index);
    const open = openPriority === key;
    const detailId = `cockpit-top-detail-${index}`;
    const detail = open ? priorityDetail(row, summaryText) : "";
    return (
      <li key={key} className={`cockpit-briefing-top-item cockpit-briefing-top-item--openable${open ? " is-open" : ""}`}>
        <button
          type="button"
          className="cockpit-briefing-top-toggle"
          aria-expanded={open}
          aria-controls={detailId}
          onClick={() => setOpenPriority(open ? null : key)}
        >
          <span className="cockpit-briefing-top-rank">{index + 1}</span>
          {topRowText(row)}
          {row.deadline ? <span className="cockpit-briefing-top-due">{row.deadline}</span> : null}
          <span className="cockpit-briefing-top-chevron" aria-hidden="true">
            <MaterialIcon icon="expand_more" size={20} color="currentColor" weight={300} />
          </span>
        </button>
        {open ? (
          <div className="cockpit-briefing-top-detail" id={detailId}>
            <p>{detail || strings.room_overview_priority_no_detail || "Der Bericht nennt keine weiteren Details zu dieser Priorität."}</p>
            {row.suggestedBy ? (
              <p className="cockpit-briefing-top-meta">
                {String(strings.room_overview_priority_from || "Quelle: {{name}}").replace("{{name}}", row.suggestedBy)}
              </p>
            ) : null}
            <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm cockpit-briefing-top-open" onClick={() => openTopTask(row)}>
              <MaterialIcon icon="task_alt" size={18} color="currentColor" weight={300} />
              {strings.room_overview_priority_open_task || "Aufgabe öffnen"}
            </button>
          </div>
        ) : null}
      </li>
    );
  };
  // "Heute wichtig": a priority as a card with its detail, who it came from
  // and the button for its task.
  const renderPriorityCard = (row, index) => {
    const detail = priorityDetail(row, summaryText);
    const project = hideProject ? "" : rowProject(row);
    return (
      <li key={topKey(row, index)} className="cockpit-priority-card">
        <div className="cockpit-priority-card-head">
          <span className="cockpit-briefing-top-rank">{index + 1}</span>
          {row.deadline ? <span className="cockpit-priority-card-due">{row.deadline}</span> : null}
        </div>
        {project ? <div className="cockpit-priority-card-project">{project}</div> : null}
        <h5 className="cockpit-priority-card-title" title={plain(row.nextStep)}>{prioritySentence(row)}</h5>
        {detail ? <p className="cockpit-priority-card-detail" title={detail}>{detail}</p> : null}
        {row.suggestedBy ? (
          <p className="cockpit-priority-card-meta">
            {String(strings.room_overview_priority_from || "Quelle: {{name}}").replace("{{name}}", row.suggestedBy)}
          </p>
        ) : null}
        <button type="button" className="kj-btn kj-btn--primary kj-btn--sm cockpit-priority-card-open" onClick={() => openTopTask(row)}>
          {strings.room_overview_priority_open_task || "Aufgabe öffnen"}
        </button>
      </li>
    );
  };
  const cardArrows = prioritiesOnly && priorityCards && topFive.length > 1 ? (
    <div className="cockpit-priority-cards-nav">
      <button
        type="button"
        className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm kj-btn--icon"
        onClick={() => scrollCards(-1)}
        disabled={!cardsScroll.back}
        aria-label={strings.previous || "Zurück"}
      >
        <MaterialIcon icon="chevron_left" size={20} color="currentColor" weight={300} />
      </button>
      <button
        type="button"
        className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm kj-btn--icon"
        onClick={() => scrollCards(1)}
        disabled={!cardsScroll.next}
        aria-label={strings.next || "Weiter"}
      >
        <MaterialIcon icon="chevron_right" size={20} color="currentColor" weight={300} />
      </button>
    </div>
  ) : null;
  // Beside the "Heute wichtig" heading: which card the row starts at.
  const cardPosition = cardArrows ? (
    <span className="cockpit-priority-cards-position" aria-live="polite">
      {String(strings.cockpit_priority_position || "{{index}} of {{total}} priorities")
        .replace("{{index}}", Math.min(cardsScroll.first + 1, topFive.length))
        .replace("{{total}}", topFive.length)}
    </span>
  ) : null;
  const fazitHeading = (
    <h4 className="cockpit-briefing-fazit-title">{fazitTitle || strings.cockpit_report_management_fazit || "Management-Fazit"}</h4>
  );
  const titleLine = cardPosition ? (
    <div className="cockpit-priority-cards-title">
      {fazitHeading}
      {cardPosition}
    </div>
  ) : fazitHeading;
  const summary = (
    <section className="cockpit-briefing-section">
      <div className="cockpit-briefing-head">
        {/* The report's own heading varies ("Management-Fazit", "Fazit", …);
            the card always labels it Management-Fazit. */}
        {fazitMeta ? (
          <div className="cockpit-briefing-title-block">
            {titleLine}
            <div className="cockpit-briefing-fazit-meta">{fazitMeta}</div>
          </div>
        ) : titleLine}
        {cardArrows ? (
          <div className="cockpit-priority-cards-tools">
            {toolbar || null}
            {cardArrows}
          </div>
        ) : toolbar || null}
      </div>
      {/* Bold as in the report: the day's main topic and each project label;
          each project starts a new line. */}
      {prioritiesOnly ? null : (fazit && fazit.paragraphs.length ? fazit.paragraphs : [""]).map((paragraph, index) => {
        const runs = fazit && fazit.rich && fazit.rich[index];
        if (!runs || !runs.length) {
          return <p key={`${index}|${paragraph}`} className="cockpit-briefing-fazit">{paragraph || "—"}</p>;
        }
        return splitAtRoomLabels(runs).map((part, partIndex) => (
          <p key={`${index}-${partIndex}`} className="cockpit-briefing-fazit">
            {part.map((run, i) => (run.bold ? <strong key={i}>{run.text}</strong> : <React.Fragment key={i}>{run.text}</React.Fragment>))}
          </p>
        ));
      })}
      {prioritiesOnly && priorityCards && topFive.length ? (
        <ol className="cockpit-priority-cards" ref={cardsRef} onScroll={measureCards}>
          {topFive.map(renderPriorityCard)}
        </ol>
      ) : prioritiesOnly && topFive.length ? (
        <div className="cockpit-briefing-top cockpit-briefing-top--only">
          <ol className="cockpit-briefing-top-list">{topFive.map(renderOpenableTop)}</ol>
        </div>
      ) : prioritiesOnly ? (
        <p className="cockpit-briefing-fazit">{strings.room_overview_no_priorities || "Keine offenen Prioritäten im letzten Bericht."}</p>
      ) : topFive.length ? (
        <div className="cockpit-briefing-top">
          <h5 className="cockpit-briefing-top-title">{strings.cockpit_report_top5 || "Top 5 Prioritäten"}</h5>
          <ol className="cockpit-briefing-top-list">
            {topFive.map((row, index) => (
              <li
                key={topKey(row, index)}
                className="cockpit-briefing-top-item"
                role="button"
                tabIndex={0}
                onClick={() => openTopTask(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openTopTask(row);
                  }
                }}
              >
                <span className="cockpit-briefing-top-rank">{index + 1}</span>
                {topRowText(row)}
                {row.deadline ? <span className="cockpit-briefing-top-due">{row.deadline}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );

  // Priorities belong to the latest report only — an older one is kept as a
  // record of what was written, so it stops at its summary text.
  if (summaryOnly) return <article className="cockpit-briefing">{summary}</article>;

  const allRows = sections.reduce((rows, section) => {
    if (section.priorities && section.priorities.length) rows.push(...section.priorities);
    return rows;
  }, []);
  // One bookmark per step is created, so the list shows one row per step even
  // when the report words the same one twice. Steps the user deleted stay off
  // the list — the report still mentions them in its text.
  const dismissedTexts = [...(dismissed || []), ...hiddenTexts];
  const priorityRows = allRows.reduce((rows, row) => {
    const text = priorityText(row);
    if (!text || findSimilar(rows, text, priorityText)) return rows;
    if (dismissedTexts.length && findSimilar(dismissedTexts, text)) return rows;
    if (rowIsDone(row)) return rows;
    rows.push(row);
    return rows;
  }, []);
  // A priority that already produced a bookmarked to-do is shown as that
  // to-do, so the report row and the task are one and the same thing.
  const taskById = new Map(openTasks.map((item) => [String(item.todoId), item]));
  // Remember which to-do a row matched: renaming it would break the text match.
  const taskFor = (key, text) => {
    const known = linkedRef.current[key];
    if (known && taskById.has(String(known))) return taskById.get(String(known));
    // A regenerated report rewords its rows, so the match is by similarity —
    // the row then shows the existing bookmark instead of offering a new one.
    const hit = findSimilar(openTasks, text, (item) => item.description);
    if (hit) linkedRef.current[key] = hit.todoId;
    return hit || null;
  };

  // The project a report row belongs to (its own room, else matched by name).
  // Only rows with one can become a task: every task belongs to a project.
  const rowRoomId = (row) => {
    if (!row) return null;
    const knownRooms = [...(Array.isArray(rooms) ? rooms : []), ...listedRooms(store.getState())];
    const room = row.roomId ? { id: row.roomId } : matchRoom(knownRooms, row.project, email);
    return (room && (room.id || room.buildingId || room._id)) || null;
  };

  const createTodo = async (row, key) => {
    if (rowState[key] === "busy" || rowState[key] === "done") return;
    const roomId = rowRoomId(row);
    if (!roomId) return;
    setRowState((prev) => ({ ...prev, [key]: "busy" }));
    try {
      const summary = row.nextStep || row.project || "Aufgabe";
      // A date keeps the priority sentence as the to-do text. Without one the
      // API treats the row as a bookmark and replaces the title.
      // The row's detail is the task's description text under its title.
      const details = priorityDetail(row, summaryText);
      const payload = {
        summary,
        details,
        messageText: details || summary,
        date: parsePriorityDeadline(row.deadline) || new Date().toISOString(),
        roomId,
      };
      const res = await callAPI("/todo/manualTodoCreate", {}, "POST", payload);
      if (res?.data?.status === false) {
        throw new Error(res.data.message || "create failed");
      }
      setRowState((prev) => ({ ...prev, [key]: "done" }));
      $crud.notify({
        type: "success",
        message: strings.cockpit_report_todo_created || "Aufgabe erstellt",
      });
      if (onCreated) onCreated(row);
    } catch (error) {
      setRowState((prev) => ({ ...prev, [key]: "error" }));
      $crud.notify({
        type: "error",
        message: (error && error.message) || strings.todo_creation_failed || "Aufgabe konnte nicht erstellt werden",
      });
    }
  };

  // The report's own priorities first, in report order; anything the user
  // bookmarked themselves follows in the same list.
  const usedTaskIds = new Set();
  const priorityItems = priorityRows
    .map((row, index) => {
      const key = `${row.project}|${row.nextStep}|${index}`;
      const found = taskFor(key, priorityText(row));
      // A to-do belongs to one row; a second near-identical row cannot claim it.
      const task = found && !usedTaskIds.has(String(found.todoId)) ? found : null;
      if (task) usedTaskIds.add(String(task.todoId));
      return { key, row, task };
    });
  const ownTasks = openTasks.filter((task) => !usedTaskIds.has(String(task.todoId)));
  const notedTexts = noted ? [...noted] : [];

  const entryCategory = (entry) => {
    const category = (entry.task && entry.task.bookmarkCategory) || (entry.row && entry.row.kind);
    return BOOKMARK_CATEGORIES.includes(category) ? category : "other";
  };
  const inTaskGroup = (entry) => !taskGroups || taskGroups.includes(cockpitGroupOf(entryCategory(entry)));
  const entryDeadline = (entry) =>
    (entry.task && entry.task.bookmarkDeadline) || (entry.row ? priorityDateValue(entry.row) : "") || "";
  // The day as the list has it: a dated task by its date, a bookmark by its
  // deadline, a report row by the report's.
  const entryDay = (entry) => {
    const task = entry.task;
    if (task && task.isBookmarked != null && !Number(task.isBookmarked)) return localDayKey(task.createdAt);
    return entryDeadline(entry);
  };

  // Today's list: a task (and its report row) moved to a later day leaves it.
  // Another day of the strip (`taskDay`) lists only what falls on that day.
  const reportEntries = priorityItems.concat(ownTasks.map((task) => ({ key: `task-${task.todoId}`, row: null, task })));
  const todayEntries = reportEntries.filter((entry) => !dueAfterToday(entry.task));
  const shownEntries = taskDay ? reportEntries.filter((entry) => entryDay(entry) === taskDay) : todayEntries;

  // Both kinds of row under one key each, in render order, so "select all"
  // and the bulk delete work on exactly the list the user is looking at.
  const allEntries = shownEntries.filter(inTaskGroup);
  const selecting = !!selection;
  const selectedCount = selection ? selection.size : 0;
  const allSelected = selecting && !!allEntries.length && selectedCount === allEntries.length;
  const toggleSelected = (key) =>
    setSelection((current) => {
      const next = new Set(current || []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const withCount = (key, fallback, total) => String(strings[key] || fallback).replace("{count}", total);

  const notifySourceMissing = (type = "error") => {
    $crud.notify({
      type,
      message: strings.cockpit_report_source_missing || "The source message could not be found.",
    });
  };

  // Always via Nachrichten, never the room: the conversation opens next to
  // the chat list and scrolls to the message, wherever the chat lives.
  const openChatTarget = async (target, room) => {
    const located = await locateChatMessage(target.groupId, target.messageId);
    const roomId = located.roomId || (room ? room.id : null);
    openChatMessage(dispatch, history, {
      chatGroupId: String(target.groupId),
      messageId: target.messageId ? String(target.messageId) : null,
      page_no: located.page || target.page || 1,
      roomId,
      companyId: located.companyId || (room && room.isInternal ? room.companyId : null),
      isInternal: !!(located.companyId || (room && room.isInternal)),
      isOneToOne: located.isOneToOne,
      name: located.name,
      ...(located.isTeams ? { isTeams: true, teamsChatType: located.teamsChatType, webUrl: located.webUrl } : {}),
    });
  };

  const openSource = async (rowIn, key, task) => {
    if (sourceBusyRef.current) return;
    sourceBusyRef.current = true;
    setSourceState((prev) => ({ ...prev, [key]: "busy" }));
    let row = rowIn;
    try {
      // A row without its source (older reports, a fact from an attachment):
      // the server matches the task against what the report read and keeps
      // the answer on the task.
      if (!row.emailId && !row.groupId && task && task.todoId != null) {
        const { data } = await callAPI(`/todo/${task.todoId}/source`, {}, "POST", {}).catch(() => ({ data: null }));
        if (data && data.status && data.data) row = { ...row, ...data.data };
      }
      if (row.emailId) {
        openEmail(dispatch, history, { id: row.emailId, emailAccountId: row.emailAccountId || null });
        return;
      }
      const state = store.getState();
      const room = resolveRoom(row, rooms, email, state);
      if (row.groupId) {
        await openChatTarget({ groupId: row.groupId, messageId: row.chatId, page: row.chatPage }, room);
        return;
      }
      if (!room) {
        notifySourceMissing();
        return;
      }
      const reportAt = timeOf(email && (email.receivedAt || email.date || email.createdAt)) || Date.now();
      const openMail = (mail) => openEmail(dispatch, history, { id: mail.id, emailAccountId: mail.emailAccountId });
      const mails = await emailCandidates(room);
      if (sourceKind(row) === "email") {
        const mail = bestSource(mails, row, reportAt);
        if (mail) {
          openMail(mail);
          return;
        }
      }
      const chats = await chatCandidates(room, reportAt, state.user && state.user.id);
      const best = bestSource(mails.concat(chats.list), row, reportAt);
      if (best && best.kind === "email") {
        openMail(best);
        return;
      }
      if (best) {
        await openChatTarget(best, room);
        return;
      }
      if (chats.groupId) {
        notifySourceMissing("info");
        await openChatTarget({ groupId: chats.groupId }, room);
        return;
      }
      notifySourceMissing();
    } catch (error) {
      notifySourceMissing();
    } finally {
      sourceBusyRef.current = false;
      setSourceState((prev) => ({ ...prev, [key]: "" }));
    }
  };

  // Deleting a report priority removes the row itself, not just the task it
  // produced: the text is recorded as dismissed so neither this render nor the
  // next sync pass brings it back.
  const dismissRow = (row) => (onDismiss ? onDismiss(priorityText(row)) : Promise.resolve());

  const hideTexts = (texts) => setHiddenTexts((prev) => prev.concat(texts));
  // Once the dismissal is stored (or failed), the local hiding is no longer needed.
  const unhideTexts = (texts) => setHiddenTexts((prev) => prev.filter((text) => !texts.includes(text)));

  const deleteTask = async (row, item) => {
    const text = priorityText(row);
    const removed = await tasks.remove(item, { onStart: () => hideTexts([text]) });
    try {
      if (removed) await dismissRow(row);
    } finally {
      unhideTexts([text]);
    }
    return removed;
  };

  // A row the user never turned into a task has nothing to delete on the
  // server, so it only needs the same confirmation before it goes.
  const deleteRow = async (row) => {
    try {
      await $crud.confirm({
        title: "",
        textContent:
          strings.delete_bookmarked_priority_confirm
          || "Möchtest du diese vorgemerkte Aufgabe wirklich löschen?",
        options: { ok: strings.delete_label || "Löschen", cancel: strings.cancel || "Abbrechen" },
      });
    } catch (_) {
      return;
    }
    const text = priorityText(row);
    hideTexts([text]);
    try {
      await dismissRow(row);
    } finally {
      unhideTexts([text]);
    }
  };

  // The selection is deleted as one operation: asked for once, and recorded
  // in a single write however many rows it holds.
  const deleteSelected = async () => {
    const chosen = allEntries.filter((entry) => selection.has(entry.key));
    if (!chosen.length || bulkBusy) return;
    try {
      await $crud.confirm({
        title: "",
        textContent: withCount(
          "cockpit_priorities_delete_confirm",
          "Möchtest du {count} vorgemerkte Aufgaben wirklich löschen?",
          chosen.length
        ),
        options: { ok: strings.delete_label || "Löschen", cancel: strings.cancel || "Abbrechen" },
      });
    } catch (_) {
      return;
    }
    setBulkBusy(true);
    // The whole selection leaves the list at once.
    const chosenTexts = chosen.filter((entry) => entry.row).map((entry) => priorityText(entry.row));
    hideTexts(chosenTexts);
    setSelection(null);
    try {
      // The tasks go first. A row whose task survived has to stay on the
      // list, so only the ones that really went are recorded as dismissed.
      const withTask = chosen.filter((entry) => entry.task);
      const removed = withTask.length ? await tasks.removeMany(withTask.map((entry) => entry.task)) : [];
      const removedIds = new Set(removed.map((item) => String(item.todoId)));
      const texts = chosen
        .filter((entry) => entry.row && (!entry.task || removedIds.has(String(entry.task.todoId))))
        .map((entry) => priorityText(entry.row))
        .filter(Boolean);
      if (texts.length && onDismiss) await onDismiss(texts);
      const failed = withTask.length - removed.length;
      setSelection(null);
      $crud.notify({
        type: failed ? "error" : "success",
        message: failed
          ? withCount("cockpit_priorities_delete_failed", "{count} Aufgaben konnten nicht gelöscht werden", failed)
          : withCount("cockpit_priorities_deleted", "{count} Aufgaben gelöscht", chosen.length),
      });
    } finally {
      unhideTexts(chosenTexts);
      setBulkBusy(false);
    }
  };

  const inTasksCard = (node) => (tasksTarget ? createPortal(node, tasksTarget) : node);

  // "Zur Quelle navigieren" as the first entry of the row's "…" menu.
  const sourceAction = (row, key, task) => {
    const opening = sourceState[key] === "busy";
    const button = SOURCE_BUTTON[sourceKind(row)];
    return [{
      key: "source",
      icon: opening ? "hourglass_empty" : button.icon,
      label: strings[button.label] || button.fallback,
      disabled: opening,
      onClick: () => openSource(row, key, task),
    }];
  };

  // Under the cockpit's two headers, "Fristen & Aufgaben" and "Termine" (the
  // category set by the server decides), then by deadline, earliest first;
  // equal deadlines keep critical points before decisions before the rest, and
  // rows without one keep their order at the end.
  const groupTitles = cockpitGroupTitles(strings);
  const orderedEntries = shownEntries
    .map((entry, index) => ({ entry, index, due: entryDeadline(entry), rank: BOOKMARK_CATEGORIES.indexOf(entryCategory(entry)) }))
    .sort((a, b) => (a.due && b.due ? a.due.localeCompare(b.due) : a.due ? -1 : b.due ? 1 : 0) || a.rank - b.rank || a.index - b.index)
    .map(({ entry }) => entry);
  // Every group switched on (Meine Aufgaben's columns), and those with rows.
  const columnGroups = COCKPIT_TASK_GROUPS
    .map((group) => ({
      category: group.key,
      title: groupTitles[group.key],
      entries: orderedEntries.filter((entry) => cockpitGroupOf(entryCategory(entry)) === group.key),
    }))
    .filter((group) => !taskGroups || taskGroups.includes(group.category));
  const groupedEntries = columnGroups.filter((group) => group.entries.length);
  // The chips count the day shown.
  const inGroup = (key) => (entry) => cockpitGroupOf(entryCategory(entry)) === key;
  groupCountsRef.current = {
    appointment: orderedEntries.filter(inGroup("appointment")).length,
    tasks: orderedEntries.filter(inGroup("tasks")).length,
  };

  const renderReportEntry = ({ row, key, task }) => {
    const state = rowState[key];

    if (task) {
      return (
        <BookmarkRow
          key={key}
          item={task}
          strings={strings}
          rooms={rooms}
          defaultDate={priorityDateValue(row) || task.bookmarkDeadline || ""}
          busy={tasks.busyId === task.todoId || bulkBusy}
          actions={sourceAction(row, key, task)}
          selecting={selecting}
          selected={selecting && selection.has(key)}
          onSelect={() => toggleSelected(key)}
          onComplete={tasks.complete}
          onSchedule={tasks.schedule}
          onRename={tasks.rename}
          onAssign={tasks.assign}
          onAnswerSuggestion={tasks.answerSuggestion}
          onOpen={openTaskInProject}
          onSetRoom={tasks.setRoom}
          onDelete={(item) => deleteTask(row, item)}
          calendar={calendar.optionFor(task)}
          onCalendar={calendar.toggle}
        />
      );
    }

    // No task yet — same row, but the scheduler slot offers to create one.
    const isNoted = state !== "done" && !!notedTexts.length && !!findSimilar(notedTexts, priorityText(row));
    const picked = selecting && selection.has(key);
    // Same look as the task rows (.tdp-row); no tick, there is no task yet.
    return (
      <div key={key} className="tdp cockpit-task">
        <div
          className={`tdp-row cockpit-task-row${picked ? " is-adj-selected" : ""}`}
          onClick={
            selecting
              ? (e) => (e.target.closest("button, input, a, textarea, select") ? null : toggleSelected(key))
              : undefined
          }
        >
          <span className="tdp-check">
            {selecting ? (
              <input
                type="checkbox"
                className="cockpit-prio-check"
                checked={picked}
                aria-label={row.nextStep || strings.select_all || "Auswählen"}
                onChange={() => toggleSelected(key)}
              />
            ) : null}
          </span>
          <div className="tdp-row-body">
            {rowProject(row) ? <div className="tdp-row-room">{rowProject(row)}</div> : null}
            <div className="tdp-row-title">{row.nextStep}</div>
            {row.deadline || row.suggestedBy ? (
              <div className="tdp-row-meta">
                <span className="tdp-row-time">{[row.deadline, row.suggestedBy].filter(Boolean).join(" · ")}</span>
              </div>
            ) : null}
          </div>
          <div className="cockpit-prio-end">
          <RowMenu
            strings={strings}
            items={[
              // The sync makes the task on its own; this is the manual
              // fallback while it has not (yet) succeeded.
              // A row without a project stays report text (no task).
              isNoted || state === "done" || !rowRoomId(row)
                ? null
                : {
                  key: "create",
                  icon: "add_task",
                  label: strings.cockpit_report_create_todo || "Aufgabe erstellen",
                  disabled: state === "busy",
                  onClick: () => createTodo(row, key),
                },
              ...sourceAction(row, key),
              { key: "delete", icon: "delete", label: strings.delete_label || "Löschen", danger: true, onClick: () => deleteRow(row) },
            ]}
          />
          </div>
        </div>
      </div>
    );
  };

  const renderOwnTask = (task) => {
    const row = sourceRowFromTask(task);
    return (
      <BookmarkRow
        key={task.todoId}
        item={task}
        strings={strings}
        rooms={rooms}
        busy={tasks.busyId === task.todoId || bulkBusy}
        // A report task without a stored source is looked up on the server.
        actions={
          row
            ? sourceAction(row, `task-${task.todoId}`, task)
            : task.bookmarkCategory
              ? sourceAction({ nextStep: task.description || "" }, `task-${task.todoId}`, task)
              : null
        }
        defaultDate={task.bookmarkDeadline || ""}
        selecting={selecting}
        selected={selecting && selection.has(`task-${task.todoId}`)}
        onSelect={() => toggleSelected(`task-${task.todoId}`)}
        onComplete={tasks.complete}
        onSchedule={tasks.schedule}
        onRename={tasks.rename}
        onAssign={tasks.assign}
        onAnswerSuggestion={tasks.answerSuggestion}
        onOpen={openTaskInProject}
        onSetRoom={tasks.setRoom}
        onDelete={tasks.remove}
        calendar={calendar.optionFor(task)}
        onCalendar={calendar.toggle}
      />
    );
  };

  const bulkControls = allEntries.length ? (
    selecting ? (
      <div className="cockpit-prio-bulk">
        <span className="cockpit-prio-bulk-count">
          {withCount("cockpit_priorities_selected", "{count} ausgewählt", selectedCount)}
        </span>
        <button
          type="button"
          className="cockpit-bookmark-ghost-btn"
          disabled={bulkBusy}
          onClick={() => setSelection(allSelected ? new Set() : new Set(allEntries.map((e) => e.key)))}
        >
          {allSelected
            ? strings.deselect_all || "Alle abwählen"
            : strings.select_all || "Alle auswählen"}
        </button>
        <button
          type="button"
          className="cockpit-bookmark-ghost-btn cockpit-bookmark-ghost-btn--danger"
          disabled={bulkBusy || !selectedCount}
          onClick={deleteSelected}
        >
          {bulkBusy
            ? strings.cockpit_priorities_deleting || "Wird gelöscht …"
            : strings.delete_label || "Löschen"}
        </button>
        <button
          type="button"
          className="cockpit-bookmark-ghost-btn"
          disabled={bulkBusy}
          onClick={() => setSelection(null)}
        >
          {strings.cancel || "Abbrechen"}
        </button>
      </div>
    ) : (
      <button
        type="button"
        className="kj-btn kj-btn--icon kj-btn--neutral cockpit-select-many"
        title={strings.cockpit_priorities_select || "Mehrere löschen"}
        aria-label={strings.cockpit_priorities_select || "Mehrere löschen"}
        onClick={() => setSelection(new Set())}
      >
        <MaterialIcon icon="delete_sweep" size={20} color="currentColor" weight={300} />
      </button>
    )
  ) : null;

  return (
    <article className="cockpit-briefing">
      {summary}
      {calendar.modal}
      {inTasksCard(
      <section className="cockpit-briefing-section" id={BOOKMARKED_PRIORITIES_ID}>
        {tasksTarget && tasksActionTarget ? (
          createPortal(bulkControls, tasksActionTarget)
        ) : tasksTarget ? (
          bulkControls ? <div className="cockpit-briefing-head cockpit-briefing-head--end">{bulkControls}</div> : null
        ) : (
          <div className={`cockpit-briefing-head${selecting && allEntries.length ? " is-selecting" : ""}`}>
            <h4>{strings.cockpit_bookmarked_priorities || "Vorgemerkte Aufgaben"}</h4>
            {bulkControls}
          </div>
        )}
        {tasksTarget && (orderedEntries.length || taskDay) ? (
          // Meine Aufgaben: "Fristen & Aufgaben" left, "Termine" right — both
          // columns stay while switched on, an empty one says so.
          <div className={`cockpit-briefing-columns${columnGroups.length > 1 ? " cockpit-briefing-columns--two" : ""}`}>
            {columnGroups.map((group) => (
              <div key={group.category} className="cockpit-briefing-priorities">
                <div className="cockpit-bookmark-group">
                  <span>{group.title}</span>
                  <span className="cockpit-bookmark-group-count">{group.entries.length}</span>
                </div>
                {group.entries.length ? (
                  group.entries.map((entry) => (entry.row ? renderReportEntry(entry) : renderOwnTask(entry.task)))
                ) : (
                  <p className="cockpit-briefing-column-empty">
                    {group.category === "appointment"
                      ? strings.room_overview_appointments_empty || "Keine anstehenden Termine"
                      : strings.room_overview_tasks_empty || "Keine offenen Fristen oder Aufgaben"}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : groupedEntries.length ? (
          <div className="cockpit-briefing-priorities">
            {groupedEntries.map((group) => (
              <React.Fragment key={group.category}>
                <div className="cockpit-bookmark-group">
                  <span>{group.title}</span>
                  <span className="cockpit-bookmark-group-count">{group.entries.length}</span>
                </div>
                {group.entries.map((entry) => (entry.row ? renderReportEntry(entry) : renderOwnTask(entry.task)))}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <p>{strings.cockpit_report_none || "Keine Einträge."}</p>
        )}
      </section>
      )}
    </article>
  );
}
